import { api } from "./api";

/**
 * Enrich an address with RentCast property data and decide the correct
 * service-type tier (residential / apartments / commercial).
 *
 * Behaviour rules — verbatim from the original Address.jsx flow so every
 * entry point produces the SAME routing decision:
 *
 *   1. If RentCast flags the property as commercial → route to "commercial"
 *      regardless of the requested type (commercial customers get the
 *      3,000 kWh plan tier, residential would be miss-priced).
 *
 *   2. If RentCast detects an apartment / multi-family / condo profile
 *      (profile_id starts with "apt_") AND the requested type is NOT
 *      already "apartments" → route to "apartments" (1,000 kWh tier).
 *      We never DOWNGRADE an explicit "apartments" request: when the
 *      user clicked the Apartments card or the address has "APT" /
 *      unit hints, we trust that signal over RentCast's silent
 *      `res_med` default.
 *
 *   3. The detected `profile_id` is persisted in localStorage under
 *      `velcor_property_profile` so the /plans page can pre-select
 *      the matching tier (eco / unlimited / etc.).
 *
 *   4. The call is fully non-blocking: any HTTP / network / timeout
 *      error is swallowed and we fall back to the originally requested
 *      type. RentCast must NEVER stop a customer from continuing.
 *
 * @param {object} args
 * @param {string} args.address  Full street address (canonical, post-ESIID-pick).
 * @param {string} args.city
 * @param {string} args.zip
 * @param {string} args.type     Requested service type from the previous page
 *                               ("residential" | "apartments" | "commercial").
 * @returns {Promise<{routedType: string, profileId: string|null}>}
 */
export async function enrichWithRentCast({ address, city = "", zip = "", type = "residential" }) {
  let routedType = type;
  let profileId = null;
  if (!address) return { routedType, profileId };
  try {
    const { data: rc } = await api.post("/property/lookup", {
      address,
      city,
      state: "TX",
      zip,
    });
    if (rc?.tier?.is_commercial && routedType !== "commercial") {
      routedType = "commercial";
    } else if (
      rc?.tier?.profile_id?.startsWith("apt_") &&
      routedType !== "apartments"
    ) {
      routedType = "apartments";
    }
    if (rc?.tier?.profile_id) {
      profileId = rc.tier.profile_id;
      try { localStorage.setItem("velcor_property_profile", profileId); } catch { /* SSR */ }
    }
  } catch (err) {
    // Silent — never block the customer flow when RentCast is down or
    // rate-limited. The user still proceeds with their originally
    // requested type.
    console.warn("rentcast enrichment skipped:", err?.message || err);
  }
  return { routedType, profileId };
}
